// ─────────────────────────────────────────────────────────────────────────────
// Countdown — progress bar + "X mins Y secs" text for a time-limited
// checkout.
//
// Contract:
//   expiresAt=null → render NOTHING. The creator explicitly didn't set an
//                    expiry and we must not imply one. Payer sees no
//                    countdown, no bar, no hint of urgency.
//
//   expiresAt=ISO  → compute remaining vs now, tick every second, fill the
//                    bar proportionally from 0%→100% as the window closes.
//                    Colour the fill:
//                      > 5 min remaining   → Posteering orange gradient
//                      ≤ 5 min, > 2 min    → deeper orange ("urgent")
//                      ≤ 2 min             → red ("critical")
//
// Clock source: client-side `Date.now()`. Per earlier decision, we accept
// mild clock-skew on Nigerian mobile devices (phones sync via cell tower,
// typically accurate to within seconds). A server-anchored countdown would
// require extra BFF fields and add complexity for no meaningful accuracy win.
//
// When the countdown reaches zero:
//   We show "Expired" in the text and full the bar. We do NOT mutate the
//   checkout status client-side — the status transition happens server-
//   side via lazy expiry on the next poll, which the polling hook will
//   pick up. So after countdown reaches zero, the user briefly sees
//   "Expired" + pending state, then the next 5s poll flips the whole
//   page to the <TerminalState status="expired" /> view.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useRef } from 'react';

export interface CountdownProps {
  /** ISO 8601 expiry timestamp from the BFF, or null to hide entirely. */
  expiresAt: string | null;
  /** ISO 8601 creation timestamp — anchors the "total window" for the bar. */
  createdAt: string;
}

export function Countdown({ expiresAt, createdAt }: CountdownProps) {
  // If there's no expiry, render nothing. Pass-through; hooks below still
  // need to mount/unmount cleanly in case the prop changes on a future poll.
  if (expiresAt === null) return null;

  return <CountdownTicker expiresAt={expiresAt} createdAt={createdAt} />;
}

interface TickerProps {
  expiresAt: string;  // narrowed from the parent null-check
  createdAt: string;
}

function CountdownTicker({ expiresAt, createdAt }: TickerProps) {
  const expiresMs = new Date(expiresAt).getTime();
  const createdMs = new Date(createdAt).getTime();

  // Full window from creation → expiry. Used as the bar's denominator.
  // Defensive: if the server somehow returns createdAt >= expiresAt,
  // treat the total window as 1ms (bar instantly full, tick shows Expired).
  const totalWindowMs = Math.max(expiresMs - createdMs, 1);

  const [now, setNow] = useState<number>(() => Date.now());
  const intervalRef = useRef<number | null>(null);

  useEffect(() => {
    intervalRef.current = window.setInterval(() => {
      setNow(Date.now());
    }, 500);

    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  const remainingMs = expiresMs - now;
  const expired = remainingMs <= 0;

  const elapsedMs = expired ? totalWindowMs : totalWindowMs - remainingMs;
  const percent = Math.min(100, Math.max(0, (elapsedMs / totalWindowMs) * 100));

  const fillClass = expired
    ? 'countdown-fill critical'
    : remainingMs < 2 * 60 * 1000
      ? 'countdown-fill critical'
      : remainingMs < 5 * 60 * 1000
        ? 'countdown-fill urgent'
        : 'countdown-fill';

  return (
    <div className="countdown">
      <div className="countdown-bar">
        <div className={fillClass} style={{ width: `${percent.toFixed(1)}%` }} />
      </div>
      <div className="countdown-text">
        {expired ? (
          <strong>Expired</strong>
        ) : (
          <>
            This one-time account expires in{' '}
            <strong>{formatRemaining(remainingMs)}</strong>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Format a remaining-ms duration as "X mins YY secs".
// Matches the Monnify/ICAN reference visual exactly.
// ─────────────────────────────────────────────────────────────────────────────

function formatRemaining(ms: number): string {
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins} mins ${String(secs).padStart(2, '0')} secs`;
}
